import PageHero from "../components/site/PageHero";
import CTABanner from "../components/site/CTABanner";
import useSEO from "../hooks/useSEO";
import {
  Building2,
  Store,
  Factory,
  Warehouse,
  HeartPulse,
  GraduationCap,
  Hotel,
  KeyRound,
} from "lucide-react";

const INDUSTRIES = [
  {
    icon: Building2,
    title: "Commercial Offices",
    copy: "Alarm coverage and emergency lighting for offices, mixed-use and multi-tenant developments.",
  },
  {
    icon: Store,
    title: "Retail",
    copy: "Extinguishers, alarms and testing for stores, shopping centres and customer-facing premises.",
  },
  {
    icon: Factory,
    title: "Manufacturing",
    copy: "Robust fire safety systems designed around industrial processes and higher-risk activities.",
  },
  {
    icon: Warehouse,
    title: "Warehousing",
    copy: "Large-area detection, appropriate extinguisher provision and routine inspections.",
  },
  {
    icon: HeartPulse,
    title: "Healthcare",
    copy: "Sensitive fire safety design for clinical environments and continuous-occupancy premises.",
  },
  {
    icon: GraduationCap,
    title: "Education",
    copy: "Schools and campuses requiring reliable alarm coverage and scheduled testing.",
  },
  {
    icon: Hotel,
    title: "Hospitality",
    copy: "Hotels, restaurants and hospitality venues where guest safety is a top priority.",
  },
  {
    icon: KeyRound,
    title: "Property Management",
    copy: "Managed estates and portfolios needing consistent, documented servicing across sites.",
  },
  {
    icon: Factory,
    title: "Industrial Facilities",
    copy: "Tailored solutions for high-risk industrial sites and specialist operational areas.",
  },
];

export default function Industries() {
  useSEO({
    title: "Industries We Support | Fire Safety Systems Ireland",
    description:
      "Fire Safety Systems supports commercial offices, retail, manufacturing, warehousing, healthcare, education, hospitality, property management and industrial facilities across Ireland.",
    canonicalPath: "/industries",
  });

  return (
    <>
      <PageHero
        eyebrow="Industries We Support"
        title="Tailored fire safety solutions for every sector."
        subtitle="We support organisations across a wide range of industries with dependable fire safety installation, testing, servicing and maintenance solutions."
        image="https://customer-assets-m6fa6gv7.emergentagent.net/job_fire-cctv-access/artifacts/4694gjo3_image.png"
        imageAlt="Commercial and industrial premises"
        ctaPrimary={{ to: "/contact", label: "Get a Free Quote" }}
        ctaSecondary={{ to: "/services/fire-alarms", label: "View Our Services" }}
      />

      <section className="fss-section bg-white">
        <div className="fss-container">
          <div className="max-w-2xl mb-10">
            <p className="text-slate-600 leading-relaxed">
              Different premises have different fire risks, operational
              requirements and maintenance needs. Rather than offering a
              one-size-fits-all service, we take the time to understand your
              site and provide fire safety solutions that suit the way you
              operate.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
            {INDUSTRIES.map((it, i) => (
              <div key={i} className="bg-white p-8" data-testid={`industry-card-${i}`}>
                <div className="w-12 h-12 border border-slate-200 flex items-center justify-center rounded-sm bg-slate-50">
                  <it.icon
                    className="w-6 h-6 text-green-700"
                    strokeWidth={1.75}
                  />
                </div>
                <h3 className="mt-4 font-display text-xl font-semibold text-slate-900">
                  {it.title}
                </h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                  {it.copy}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 fss-section border-y border-slate-200">
        <div className="fss-container grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="fss-overline">One partner, many sites</div>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-semibold text-slate-900 leading-tight">
              Support across single or multiple premises.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-4 text-slate-600 leading-relaxed">
            <p>
              Whether you are responsible for a single site or a portfolio of
              premises, our team can help design a consistent fire safety
              programme that maintains clear records and supports the ongoing
              reliability of every system.
            </p>
            <p>
              Talk to us about your requirements — we will help identify the
              right combination of installation, testing and maintenance for
              your sites.
            </p>
          </div>
        </div>
      </section>

      <CTABanner
        title="Industry-specific fire safety, done properly."
        subtitle="Tell us about your sector and operational requirements."
        primary={{ to: "/contact", label: "Get a Free Quote" }}
        secondary={{ to: "/about", label: "Learn More About FSS" }}
      />
    </>
  );
}
